import { useEffect } from "react";

const SCRIPT_ID = "timeflow-chatbot-script";
const HIDE_STYLE_ID = "manowar-launcher-hide-style";
const LAUNCHER_ID = "riverr-chat-launcher";
const CONTAINER_ID = "riverr-chat-container";

// Manowar injects a <button id="riverr-chat-launcher"> FAB and a
// <div id="riverr-chat-container"> panel (with the chatbot iframe)
// directly into <body>. We hide the default bottom-right launcher and
// expose window.__manowarLauncher / window.__manowarIframe for the
// emergency Manowar path (`assist.manowarRag`).

const hideLauncher = (launcher) => {
  if (!launcher) return;
  launcher.style.setProperty("display", "none", "important");
  launcher.style.setProperty("visibility", "hidden", "important");
  launcher.style.setProperty("opacity", "0", "important");
  launcher.style.setProperty("pointer-events", "none", "important");
  launcher.setAttribute("aria-hidden", "true");
  launcher.dataset.riverrHiddenLauncher = "1";
};

const ensureHideStyle = () => {
  if (document.getElementById(HIDE_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = HIDE_STYLE_ID;
  // Prefer the stable launcher id; keep the data-attr as a fallback.
  style.textContent = `
    #${LAUNCHER_ID},
    [data-riverr-hidden-launcher="1"] {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
  `;
  document.head.appendChild(style);
};

const claimNodes = () => {
  const launcher = document.getElementById(LAUNCHER_ID);
  if (launcher) {
    hideLauncher(launcher);
    window.__manowarLauncher = launcher;
  } else if (window.__manowarLauncher) {
    hideLauncher(window.__manowarLauncher);
  }

  // Emergency path watches display/opacity on this node.
  const container = document.getElementById(CONTAINER_ID);
  if (container) {
    window.__manowarIframe = container;
    return;
  }

  if (!window.__manowarIframe) {
    const iframe = Array.from(document.body.querySelectorAll("iframe")).find(
      (el) => el.src.includes("/chatbot/"),
    );
    if (iframe) window.__manowarIframe = iframe;
  }
};

// Dead path: Manowar RAG. Kept behind `assist.manowarRag` so the hosted
// iframe can be re-enabled in an emergency. Ask is the live backing
// endpoint (`/assist`). Remove this file once the flag has been off in
// production long enough.
const LoadChatBot = ({ currentUser }) => {
  const { isEnterpriseAdmin, displayName, email, uid } = currentUser;

  useEffect(() => {
    if (!isEnterpriseAdmin) return;

    ensureHideStyle();

    // Script may already be present from a prior mount — still claim/hide nodes.
    if (!document.getElementById(SCRIPT_ID)) {
      window.Manowar =
        window.Manowar ||
        function () {
          (window.ManowarArgs = window.ManowarArgs || []).push(arguments);
        };
      window.Manowar("identify", {
        user_id: uid,
        email: email,
        name: displayName,
      });

      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = "https://manowar.cloud/chatbot-loader.js";
      script.async = true;
      script.setAttribute("data-bot-id", "sLb2cnOGy6AJTjVmZfDR");
      script.setAttribute("data-hub-id", "KCAldrttjRdqN9LDLrl6");
      document.body.appendChild(script);
    }

    claimNodes();

    const observer = new MutationObserver(() => {
      claimNodes();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    // Manowar injects after settings fetch; keep claiming briefly.
    const poll = setInterval(claimNodes, 500);
    const stopPoll = setTimeout(() => clearInterval(poll), 30000);

    return () => {
      observer.disconnect();
      clearInterval(poll);
      clearTimeout(stopPoll);
      const el = document.getElementById(SCRIPT_ID);
      if (el) el.remove();
      const hideStyle = document.getElementById(HIDE_STYLE_ID);
      if (hideStyle) hideStyle.remove();
      document.getElementById(LAUNCHER_ID)?.remove();
      document.getElementById(CONTAINER_ID)?.remove();
      delete window.__manowarLauncher;
      delete window.__manowarIframe;
      delete window.__RIVERR_CHAT_INITIALIZED__;
    };
  }, [isEnterpriseAdmin, displayName, email, uid]);

  return null;
};

export default LoadChatBot;
